import { Box, Container, Snackbar, TextField } from '@mui/material';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import StepsList from '../components/common/TaskTemplatesSection/StepsList';
import TemplateFieldsSection from '../components/common/TemplateFieldsSection/TemplateFieldsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import DisableContextProvider, { DisableContext } from '../contexts/DisableContextProvider';
import usePermissionAllowed from '../hooks/usePermissionAllowed';
import { Actions } from '../services/react-query/permissions';
import { useCreateTaskTemplate, useGetTaskTemplateById, useUpdateTaskTemplate } from '../services/react-query/task_templates';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { TemplateField } from '../types/FieldCommon';
import { Step } from '../types/Step';
import { TaskTemplate } from '../types/TaskTemplate';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator } from '../utility/inputUtils';
import TagsSection from '../components/common/TagsSection/TagsSection';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import { useArchiveTaskTemplate } from '../services/react-query/task_templates/useArchiveTaskTemplate';
import { ROUTE_PATHS } from '../routes/Routes';
import { useDuplicateTaskTemplate } from '../services/react-query/task_templates/useDuplicateTaskTemplate';
import _ from 'lodash';
import AccordionSection from '../components/AccordionSection';
import { useIsMutating } from '@tanstack/react-query';
import { mutationKeys } from '../services/react-query/mutationKeys';
import DuplicateModal from '../components/DuplicateModal';

export default function TaskTemplateItem() {
  const { uuid } = useParams();
  const [taskTemplate, setTaskTemplate] = useState<TaskTemplate | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const navigate = useNavigate();

  const { mutate: archiveTaskTemplate } = useArchiveTaskTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.TASK_TEMPLATES}`),
  });

  const { data, isLoading } = useGetTaskTemplateById({ uuid });

  const { mutate: createTaskTemplate } = useCreateTaskTemplate({ onSuccessCallback: () => null });
  const { mutate: updateTaskTemplate } = useUpdateTaskTemplate({
    uuid,
    onSuccessCallback: () => setSaved(true),
  });
  const isCreateTaskTemplateMutation = useIsMutating({ mutationKey: [mutationKeys.createTaskTemplate] });
  const isUpdateTaskTemplateMutation = useIsMutating({ mutationKey: [mutationKeys.updateTaskTemplate] });

  const { mutate: duplicateTemplateMutation } = useDuplicateTaskTemplate((newUuid) => {
    navigate(`/task-templates/${newUuid}`);
  });

  useEffect(() => {
    if (data) setTaskTemplate(data);
  }, [data]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable(!!taskTemplate?.discarded_at);

  if (isLoading || taskTemplate === null) return <Loading />;

  const handleSave = () => {
    if (uuid === 'new') {
      createTaskTemplate(taskTemplate);
    } else {
      updateTaskTemplate(taskTemplate);
    }
  };

  const setFields = (fields: TemplateField[]) => setTaskTemplate({ ...taskTemplate, fields });

  const handleDuplicate = (name: string) => {
    duplicateTemplateMutation({ uuid: _.toString(uuid), name });
  };

  return (
    <Container>
      <ItemHeader
        title={`${uuid === 'new' ? 'Create' : 'Update'} Task Template`}
        onSave={handleSave}
        archived={!!taskTemplate.discarded_at}
        disableActions={Boolean(isCreateTaskTemplateMutation || isUpdateTaskTemplateMutation)}
        showMoreOptions={
          uuid !== 'new' ? (
            <MoreOptions onArchive={archiveTaskTemplate} onDuplicate={() => setIsDuplicateModalOpen(true)} name={taskTemplate?.name} itemName="task template" />
          ) : null
        }
      />
      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.TASK_TEMPLATES_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          <Box sx={inputDividerStyling}>
            <TextField
              id="name"
              label="Name"
              autoFocus
              onChange={(event) => setTaskTemplate(taskTemplate ? { ...taskTemplate, name: event.target.value } : null)}
              value={taskTemplate?.name ?? ''}
              disabled={disabled}
              {...errorPopulator(taskTemplate, 'name')}
            />
          </Box>
          <Box sx={inputDividerStyling}>
            <TextField
              id="description"
              label="Description"
              onChange={(event) => setTaskTemplate(taskTemplate ? { ...taskTemplate, description: event.target.value } : null)}
              value={taskTemplate?.description ?? ''}
              disabled={disabled}
              {...errorPopulator(taskTemplate, 'description')}
            />
          </Box>
        </Box>
      </AccordionSection>

      <StepsList steps={taskTemplate.steps || []} setSteps={(newSteps: Step[]) => setTaskTemplate({ ...taskTemplate, steps: newSteps })} />

      <TemplateFieldsSection fields={taskTemplate.fields} setFields={setFields} collapseId={UserSettingsCollapseKeys.TASK_TEMPLATES_ITEM_FIELDS} />

      <TagsSection
        tags={taskTemplate.tags}
        collapseId={UserSettingsCollapseKeys.TASK_TEMPLATES_ITEM_TAGS}
        setTags={(tags) => setTaskTemplate({ ...taskTemplate, tags })}
      />

      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(name) => handleDuplicate(name)}
        defaultName={`${taskTemplate?.name ?? ''} Copy`}
        itemName="task template"
      />
      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Container>
  );
}
